import { useRef, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Alert } from '@/components/notifications/Alert/Alert';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { getGraphQLValidationIssues, type GraphQLValidationIssue } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { TenantInformationDocument } from '@/modules/tenant/graphql/TenantInformation.generated';
import { ROUTE_KEYS } from '@/navigation/constants';
import { AdminFormBuilder } from './AdminFormBuilder';
import { useAdminFormQuery, usePublishFormMutation, useUpdateFormMutation } from './AdminFormsPage.generated';
import { FormDraft, hydrateFormDraft } from './AdminFormsPage.model';
import { useCreateFormTestSnapshotMutation, useRunFormTestMutation } from './AdminFormTests.generated';
import { AdminFormTestWorkspace, canRunFormTest, FormTestCase, TestSnapshot } from './AdminFormTestWorkspace';
interface PendingFormTest {
  snapshot: TestSnapshot;
  cases: FormTestCase[];
}
export function AdminFormEditorPage() {
  const formId = useRequiredParam('formId');
  const [validationIssues, setValidationIssues] = useState<GraphQLValidationIssue[]>([]);
  const {
    navigate
  } = useNavigateToRoute();
  const {
    showFeedback
  } = useFeedback();
  const {
    reportError
  } = useReportError();
  const {
    data,
    error,
    loading,
    refetch
  } = useAdminFormQuery({
    variables: {
      id: formId
    },
    // Do not seed the mutable editor from an older Apollo cache snapshot.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  });
  useReportError('Failed to load form', error);
  const [updateForm, {
    loading: isSaving
  }] = useUpdateFormMutation();
  const [publishForm, {
    loading: isPublishing
  }] = usePublishFormMutation();
  const [createSnapshot, {
    loading: isSnapshotting
  }] = useCreateFormTestSnapshotMutation();
  const [pendingTest, setPendingTest] = useSessionStorage<PendingFormTest | undefined>(`form-test-${formId}-pending`, undefined);
  const [runTest, {
    loading: isLaunching
  }] = useRunFormTestMutation();
  const [showTests, setShowTests] = useState(!!pendingTest);
  const [draftToTest, setDraftToTest] = useState<FormDraft>();
  const creatingTest = useRef(false);
  const formNode = data?.node?.__typename === 'Form' ? data.node : undefined;
  const draftConfig: unknown = formNode?.draftConfig;
  const draft = formNode ? hydrateFormDraft({
    ...formNode,
    draftConfig
  }) : undefined;
  const returnToList = () => navigate(ROUTE_KEYS.ADMIN_FORMS, {});
  const showValidationIssues = (error_0: unknown): boolean => {
    const issues = getGraphQLValidationIssues(error_0);
    if (issues.length === 0) return false;
    setValidationIssues(issues);
    showFeedback('Fix the form validation issues before continuing.', {
      variant: 'error'
    });
    return true;
  };
  if (loading) {
    return <SubpageLayout heading="Review">
        <Loader />
      </SubpageLayout>;
  }
  if (error) {
    return <SubpageLayout heading="Review could not be loaded" actions={<Button size="sm" variant="secondary" onClick={returnToList}>
            Back to reviews
          </Button>}>
        <Alert severity="error" alertActionButton={{
        label: 'Try again',
        buttonProps: {
          onClick: async () => {
            await refetch();
          }
        }
      }}>
          Check your connection and try again.
        </Alert>
      </SubpageLayout>;
  }
  if (!formNode) {
    return <SubpageLayout heading="Review unavailable" actions={<Button size="sm" variant="secondary" onClick={returnToList}>
            Back to reviews
          </Button>}>
        <Alert severity="warning">
          This review is unavailable. Return to Reviews and select another
          review.
        </Alert>
      </SubpageLayout>;
  }
  if (!draft) {
    return <SubpageLayout heading="Review unavailable" actions={<Button size="sm" variant="secondary" onClick={returnToList}>
            Back to reviews
          </Button>}>
        <Alert severity="warning">
          This review uses an unsupported draft schema. Return to Reviews and
          select another review.
        </Alert>
      </SubpageLayout>;
  }
  return <AdminFormBuilder key={`${draft.id}-${draft.updatedAt}`} initialDraft={draft} isPublishing={isPublishing} isSaving={isSaving || isSnapshotting} validationIssues={validationIssues} onDismissValidationIssues={() => setValidationIssues([])} onCancel={returnToList} onTestHistory={() => navigate(ROUTE_KEYS.ADMIN_FORM_TESTS, {
    formId
  })} testWorkspace={showTests && <AdminFormTestWorkspace initialCases={pendingTest?.cases} locked={!!pendingTest} isRunning={isSaving || isSnapshotting || isLaunching} onClose={() => setShowTests(false)} onRun={async cases => {
    if (creatingTest.current || !canRunFormTest(cases)) return;
    creatingTest.current = true;
    let test = pendingTest;
    try {
      if (!test) {
        if (!draftToTest) return;
        await updateForm({
          variables: {
            input: {
              id: draftToTest.id,
              update: {
                name: draftToTest.name,
                description: draftToTest.description,
                draftConfig: draftToTest.draftConfig
              }
            }
          }
        });
        const result = await createSnapshot({
          variables: {
            input: {
              id: draftToTest.id
            }
          }
        });
        const snapshot = result.data?.createFormTestSnapshot;
        if (!snapshot) throw new Error('The test snapshot was not returned');
        test = {
          snapshot,
          cases
        };
        setPendingTest(test);
      }
      await runTest({
        variables: {
          input: {
            formVersionID: test.snapshot.id,
            cases: test.cases.map(({
              householdID,
              sourceDocumentIDs
            }) => ({
              householdID,
              sourceDocumentIDs
            }))
          }
        }
      });
      setPendingTest(undefined);
      navigate(ROUTE_KEYS.ADMIN_FORM_TEST_RUN, {
        formId,
        versionId: test.snapshot.id
      });
    } catch (testError) {
      showFeedback(test ? 'The launch was not confirmed. Retry sends the same snapshot and cases.' : 'Could not prepare the form test. No extraction was requested.', {
        variant: 'error'
      });
      reportError('Failed to run form test', testError instanceof Error ? testError : new Error(String(testError)));
    } finally {
      creatingTest.current = false;
    }
  }} />} onTestDraft={async updatedDraft => {
    setDraftToTest(updatedDraft);
    setShowTests(true);
  }} onSaveDraft={async updatedDraft_0 => {
    setValidationIssues([]);
    const result_0 = await updateForm({
      variables: {
        input: {
          id: updatedDraft_0.id,
          update: {
            name: updatedDraft_0.name,
            description: updatedDraft_0.description,
            draftConfig: updatedDraft_0.draftConfig
          }
        }
      }
    }).catch((saveError: unknown) => {
      if (showValidationIssues(saveError)) return undefined;
      showFeedback('Failed to save draft. Reload the form and try again.', {
        variant: 'error'
      });
      reportError('Failed to save form draft', saveError instanceof Error ? saveError : new Error(String(saveError)));
      return undefined;
    });
    if (!result_0) return;
    showFeedback('Draft saved', {
      variant: 'success'
    });
    returnToList();
  }} onPublish={async (updatedDraft_1, hasUnsavedChanges) => {
    setValidationIssues([]);
    if (hasUnsavedChanges) {
      const saveResult = await updateForm({
        variables: {
          input: {
            id: updatedDraft_1.id,
            update: {
              name: updatedDraft_1.name,
              description: updatedDraft_1.description,
              draftConfig: updatedDraft_1.draftConfig
            }
          }
        }
      }).catch((saveError_0: unknown) => {
        if (showValidationIssues(saveError_0)) return undefined;
        showFeedback('Failed to save draft before publishing. Reload the form and try again.', {
          variant: 'error'
        });
        reportError('Failed to save form draft before publishing', saveError_0 instanceof Error ? saveError_0 : new Error(String(saveError_0)));
        return undefined;
      });
      if (!saveResult) return;
    }
    const publishResult = await publishForm({
      variables: {
        input: {
          id: updatedDraft_1.id
        }
      },
      refetchQueries: [TenantInformationDocument],
      awaitRefetchQueries: true,
      // TenantInformation is excluded from automatic mutation refetches.
      onQueryUpdated: query => query.refetch(),
      // The list routes are not mounted while the editor is open. Evict
      // every forms list variant so each consumer fetches fresh data when
      // it next mounts instead of requesting an inactive query refetch.
      update: cache => {
        cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'forms'
        });
      }
    }).catch((publishError: unknown) => {
      if (showValidationIssues(publishError)) return undefined;
      showFeedback('Failed to publish form. Reload the form and try again.', {
        variant: 'error'
      });
      reportError('Failed to publish form', publishError instanceof Error ? publishError : new Error(String(publishError)));
      return undefined;
    });
    const version = publishResult?.data?.publishForm.version.version;
    if (!version) return;
    showFeedback(`Published form version ${version}`, {
      variant: 'success'
    });
    returnToList();
  }} />;
}